import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, distinctUntilChanged, distinctUntilKeyChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { CreateQuizComponent } from '@edtech-ui/dashboard/teacher/classroom/quiz/create-quiz/create-quiz.component';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { MatDialog } from '@angular/material/dialog';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { environment } from 'environments/environment';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { AddQuizDialogBoxComponent } from '../add-quiz-dialog-box/add-quiz-dialog-box.component';
import { GetAllQuiz } from '@edtech-ui/core/models/quiz-review-interface.ts/get-all-quiz';
import { DateService } from '@edtech-ui/core/services/dateService/date.service';
import { PublishWithLinkDialogBoxComponent } from '../publish-with-link-dialog-box/publish-with-link-dialog-box.component';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { ChangeQuizStatusPayload } from '@edtech-ui/core/models/quiz-review-interface.ts/quiz-status';
import { SelectAddQuizPopupComponent } from '../select-add-quiz-popup/select-add-quiz-popup.component';
import { AdminCourseModeStatus, QUIZ_TYPE_LABEL_MAP } from '@edtech-ui/constants/admin-course-mode';


@Component({
	selector: 'app-admin-quizzes',
	standalone: true,
	imports: [FormsModule, RouterModule, CommonModule, CreateQuizComponent, MatProgressSpinnerModule, ReactiveFormsModule, MatTooltipModule,
		HasPermissionDirective
	],
	templateUrl: './admin-quizzes.component.html',
	styleUrl: './admin-quizzes.component.scss'
})
export class AdminQuizzesComponent {
	isLoading = false;
	isModalOpen = false;
	isCreating = false;
	isDropdownOpen = false;
	openMenuId: string | null = null;

	page = 1;
	limit = 10;
	totalRecords = 0;
	totalPages = 0;
	visiblePages: number[] = [];
	totalQuiz: number = 0;
	pageCourse = 1;
	limitCourse = 10;
	totalPagesCourse = 0;
	loading = false;
	classroomId = environment.classroomId;
	quizzes: GetAllQuiz[] = [];
	searchText = '';
	courses: { id: string; name: string }[] = [];
	selectedCourse: { id: string; name: string } | null = null;
	subscription = new Subscription();
	search$ = new Subject<string>();
	PermissionForStudents = PermissionForStudents;
	timeFormat = DateService.APP_TIME_ONLY;
	private searchSubject = new Subject<string>();
	selectedStatus = 'all';



	quizForm = new FormGroup({
		quiz_name: new FormControl<string>('', {
			nonNullable: true,
			validators: Validators.required
		}),
		hours: new FormControl<number>(0),
		minutes: new FormControl<number>(0),
		seconds: new FormControl<number>(0),
	});


	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private quizService: QuizManagementService,
		private toastr: ToastrService,
		private dialog: MatDialog,
		private courseService: CourseManagementService
	) { }


	ngOnInit(): void {
		const roleId = localStorage.getItem('role_id');
		if (roleId === '8' || roleId === '14' || roleId === 'COMPANYADMIN') {
			this.router.navigate(['/recruiter-dashboard/jobs']);
			return;
		}

		this.route.parent?.paramMap.subscribe(params => {
			const classroomId = params.get('id');
			if (classroomId) {
				this.classroomId = +classroomId;
			}
		});


		this.searchSubject.pipe(
			debounceTime(400), distinctUntilChanged()).subscribe((value: string) => {
				this.searchText = value.trim();
				this.page = 1;
				this.getAllQuizzes();
			})
		this.getAllQuizzes();
	}


	onSearchChange(): void {
		this.searchSubject.next(this.searchText);
	}


	clearSearch(): void {
		this.searchText = '';
		this.page = 1;
		this.searchSubject.next('');
	}

	onStatusChange(): void {
		this.page = 1;
		this.getAllQuizzes();
	}


	@HostListener('document:click', ['$event'])
	handleDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;

		this.openMenuId = null;

		if (!target.closest('.search-dropdown')) {
			this.isDropdownOpen = false;
		}
	}


	get totalQuizzes(): number {
		return this.totalRecords;
	}


	get filteredCourses(): { id: string; name: string }[] {
		const search = this.searchText.trim().toLowerCase();
		if (!search) return this.courses;
		return this.courses.filter(course =>
			course.name.toLowerCase().includes(search)
		);
	}


	getAllQuizzes(): void {
		this.isLoading = true;
		this.subscription.add(
			this.quizService.getAllQuizes(this.classroomId, this.page, this.limit,
				this.searchText, undefined, undefined, this.selectedStatus as CourseStatusEnum)
				.pipe(
					tap(response => {
						if (response.Success) {
							this.quizzes = response.Data.data;
							this.page = response.Data.pagination?.page ?? 1;
							this.limit = response.Data.pagination?.limit ?? 10;
							this.totalRecords = response.Data.pagination?.totalQuizzes ?? 0;
							this.totalQuiz = response.Data.pagination?.totalQuizzes ?? 0;
							this.totalPages = response.Data.pagination?.totalPages ?? 0;
							this.updateVisiblePages();
						} else {
							this.toastr.error(response?.Message || 'Failed to load quizzes');
						}
					}),
					catchError(() => {
						this.toastr.error('Failed to fetch quizzes');
						return of(null);
					}),
					finalize(() => (this.isLoading = false))
				).subscribe()
		);
	}


	changePage(page: number): void {
		if (page < 1 || page > this.totalPages || page === this.page) return;
		this.page = page;
		this.getAllQuizzes();
	}


	updateVisiblePages(): void {
		const pages: number[] = [];
		const start = Math.max(1, this.page - 2);
		const end = Math.min(this.totalPages, this.page + 2);
		for (let i = start; i <= end; i++) {
			pages.push(i);
		}
		this.visiblePages = pages;
	}


	toggleMenu(quizId: string): void {
		this.openMenuId = this.openMenuId === quizId ? null : quizId;
	}


	onEditQuiz(quiz: GetAllQuiz): void {
		this.openMenuId = null;
		this.router.navigate(['/admin-student-platform/create-quiz', this.classroomId, quiz.quiz_id]);
	}


	onViewQuiz(quizId: string): void {
		this.router.navigate(['/admin-student-platform/admin-quiz-management', this.classroomId,
			quizId
		]);
	}


	onDeleteQuiz(quiz: GetAllQuiz): void {
		this.openMenuId = null;
		this.openDeleteQuizDialogBox(quiz.quiz_id);
	}


	openDeleteQuizDialogBox(quizId: string): void {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { quizId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.deleteQuizById(quizId);
			}
		});
	}


	deleteQuizById(quizId: string): void {
		this.subscription.add(
			this.quizService.deleteQuizById(quizId).subscribe({
				next: (res: any) => {
					if (res?.Success) {
						this.toastr.success('Quiz deleted successfully');
						this.getAllQuizzes();
					} else {
						this.toastr.error(res?.Message || 'Failed to delete quiz');
					}
				},
				error: () => {
					this.toastr.error('Server error while deleting quiz');
				}
			})
		);
	}


	onAddQuiz(): void {
		this.isModalOpen = true;
		if (!this.courses.length) {
			this.loadCourses();
		}
	}


	openModal(): void {
		this.isModalOpen = true;
	}


	closeModal(): void {
		this.isModalOpen = false;
		this.isDropdownOpen = false;
		this.searchText = '';
		this.selectedCourse = null;
		this.quizForm.reset();
	}


	openDropdown(): void {
		this.isDropdownOpen = true;
	}


	selectCourse(course: { id: string; name: string }): void {
		this.selectedCourse = course;
		this.searchText = course.name;
		this.isDropdownOpen = false;
	}


	loadCourses(): void {
		if (this.loading) return;
		this.loading = true;

		this.courseService.getCourses(this.classroomId, this.pageCourse, this.limitCourse, 'created_at', 'desc', 'all').subscribe({
			next: res => {
				this.loading = false;

				if (res?.Success && res.Data?.data) {
					const publishedCourses = res.Data.data.filter((course: any) => course.status === 'Published')
						.map((course: any) => ({
							id: course.course_id,
							name: course.course_title,
							status: course.status
						}));
					this.courses = [...this.courses, ...publishedCourses];

					this.pageCourse = res.Data.page + 1;
					this.totalPagesCourse = res.Data.total_pages;
				}
			},
			error: () => {
				this.loading = false;
			}
		});
	}


	hasMore(): boolean {
		return this.pageCourse <= this.totalPagesCourse;
	}


	addQuiz() {
		const dialogRef = this.dialog.open(AddQuizDialogBoxComponent, {
			width: '800px'
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result?.success) {
				this.getAllQuizzes();
			}
		});
	}


	goToGenerativeDetailPage(quizId: string) {
		this.router.navigate(['/admin-student-platform/view-links', quizId]);
	}


	openGenerateLinkDialogBox(quizId: string) {
		const dialogRef = this.dialog.open(PublishWithLinkDialogBoxComponent, {
			data: { quizId: quizId }
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === true) {
				this.getAllQuizzes();
			}
		});
	}


	changeQuizStatus(quizId: string) {
		const payload: ChangeQuizStatusPayload = {
			quiz_id: quizId,
			target_status: CourseStatusEnum.Published
		}
		this.subscription.add(
			this.quizService.putChangeQuizStatus(payload).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success('Your quiz has been published and is now available to students!');
						this.openGenerateLinkDialogBox(quizId)
					} else {
						this.toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again.');
					return of(null);
				})
			).subscribe()
		);
	}


	getQuizTypeLabel(type: string): string {
		return QUIZ_TYPE_LABEL_MAP[type as AdminCourseModeStatus] || type;
	}


	openSelectQuizTypeDialogBox() {
		this.dialog.open(SelectAddQuizPopupComponent);
	}


	truncateText(text: string, limit: number = 100): string {
		if (!text) return '';
		return text.length > limit ? text.substring(0, limit) + '...' : text;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}